<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title">{{ $t('Server Monitoring') }}</h1>
                </div>
                <div class="stage-page-header-actions">
                    <router-link
                        v-if="!isCustomerContext"
                        aria-label="Eintrag anlegen"
                        class="btn-stage-icon-plain"
                        to="/workspace/insights/server-monitoring/new"
                        title="Eintrag anlegen"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </router-link>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="reloadSites"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <div class="relative flex-1 min-h-0 overflow-auto">
                    <template v-if="sites.length > 0">
                        <div class="hidden sm:block h-full">
                            <div class="align-middle inline-block min-w-full border-b border-ultralight">
                                <table class="table-listview table-listview--zebra">
                                    <thead>
                                    <tr>
                                        <th class="table-listview-l-th">{{ $t('Customer') }}</th>
                                        <th class="table-listview-l-th">{{ $t('Title') }}</th>
                                        <th class="table-listview-l-th">URL</th>
                                        <th class="table-listview-l-th">{{ $t('Environment') }}</th>
                                        <th class="table-listview-l-th">{{ $t('SSL status') }}</th>
                                        <th class="table-listview-l-th">{{ $t('Health status') }}</th>
                                    </tr>
                                    </thead>
                                    <tbody class="bg-white">
                                    <tr
                                        v-for="site in sites"
                                        :key="'server-monitoring-site-row-' + site.id"
                                        class="cursor-pointer"
                                        @click="goToManage(site)"
                                    >
                                        <td class="table-listview-l-td whitespace-no-wrap">
                                            <div class="flex items-center">
                                                <img
                                                    :src="customerAvatar(site)"
                                                    :alt="customerName(site)"
                                                    class="w-7 h-7 rounded-full object-cover mr-3 flex-shrink-0"
                                                >
                                                <div class="text-sm text-gray-700 truncate">{{ customerName(site) }}</div>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-no-wrap">
                                            <div class="text-sm font-medium text-gray-700">{{ site.name }}</div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-no-wrap">
                                            <div class="text-sm text-gray-700">{{ siteUrl(site) }}</div>
                                        </td>
                                        <td class="table-listview-l-td whitespace-no-wrap text-sm text-gray-700">
                                            {{ environmentLabel(site.environment) }}
                                        </td>
                                        <td class="table-listview-l-td whitespace-no-wrap">
                                            <span
                                                :class="statusBadgeClass(Boolean(site.external && site.external.tls_ok))"
                                                :style="statusBadgeStyle(Boolean(site.external && site.external.tls_ok))"
                                            >
                                                {{ sslStatusLabel(site) }}
                                            </span>
                                        </td>
                                        <td class="table-listview-l-td whitespace-no-wrap">
                                            <span
                                                :class="healthBadgeClass(healthStatusCode(site))"
                                                :style="healthBadgeStyle(healthStatusCode(site))"
                                            >
                                                {{ healthStatusLabel(healthStatusCode(site)) }}
                                            </span>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </template>

                    <template v-else>
                        <empty-state />
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {resolveStoreAccess} from "@/utils/access-context";

export default {
    name: "server-monitoring-list",
    metaInfo() {
        return {
            title: this.$t('Server Monitoring').toString()
        }
    },
    data() {
        return {
            sites: [],
            loading: false,
        };
    },
    mounted() {
        this.reloadSites();
    },
    computed: {
        accessContext() {
            return resolveStoreAccess(this.$store);
        },
        isCustomerContext() {
            return this.accessContext.template_family === 'customer';
        }
    },
    methods: {
        reloadSites() {
            const self = this;
            self.loading = true;
            axios.get('api/workspace/insights/server-monitoring').then(function (response) {
                self.sites = Array.isArray(response.data && response.data.items)
                    ? response.data.items
                    : [];
                self.loading = false;
            }).catch(function () {
                self.loading = false;
                self.sites = [];
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$t('Server monitoring list could not be loaded').toString(),
                    type: 'error'
                });
            });
        },
        goToManage(site) {
            if (this.isCustomerContext) {
                this.$router.push('/insights/server-monitoring/' + site.id + '/manage');
                return;
            }
            this.$router.push('/workspace/insights/server-monitoring/' + site.id + '/manage');
        },
        customerName(site) {
            return site && site.owner ? site.owner : '-';
        },
        customerAvatar(site) {
            return site && site.owner_avatar ? site.owner_avatar : '/images/default/icon.png';
        },
        siteUrl(site) {
            if (!site || !site.domain) {
                return '-';
            }

            if (String(site.domain).startsWith('http://') || String(site.domain).startsWith('https://')) {
                return site.domain;
            }

            return 'https://' + site.domain;
        },
        statusBadgeClass(isPositive) {
            if (isPositive) {
                return 'inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium border';
            }

            return 'inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700';
        },
        statusBadgeStyle(isPositive) {
            return isPositive ? this.greenBadgeStyle() : {};
        },
        greenBadgeStyle() {
            return {
                color: 'rgb(255 255 255)',
                backgroundColor: 'rgb(17 165 77 / 75%)',
                borderColor: 'rgb(3 120 50 / 30%)',
            };
        },
        certDaysLabel(daysLeft) {
            if (daysLeft === null || daysLeft === undefined) {
                return '-';
            }

            return Number(daysLeft) + ' ' + this.$t('days').toString();
        },
        sslStatusLabel(site) {
            const hasTls = Boolean(site && site.external && site.external.tls_ok);
            if (!hasTls) {
                return this.$t('Error').toString();
            }

            const daysLeft = site && site.external ? site.external.cert_days_left : null;
            if (daysLeft === null || daysLeft === undefined) {
                return 'OK';
            }

            return this.$t('OK (still :days days)', {days: Number(daysLeft)}).toString();
        },
        healthStatusCode(site) {
            if (!site) {
                return 'not_available';
            }

            const hasCriticalIssue = !(site.external && site.external.reachable) || !(site.external && site.external.tls_ok) || !(site.agent && site.agent.online);
            if (hasCriticalIssue) {
                return 'alert';
            }

            const versionLag = site.repository && typeof site.repository.major_version_lag === 'string'
                ? site.repository.major_version_lag.toLowerCase()
                : '';
            const hasUpdateIssue = versionLag.includes('hinter') || versionLag.includes('behind');
            if (hasUpdateIssue) {
                return 'update_recommended';
            }

            return 'stable';
        },
        healthStatusLabel(statusCode) {
            if (statusCode === 'not_available') {
                return '-';
            }
            if (statusCode === 'alert') {
                return this.$t('Alert').toString();
            }
            if (statusCode === 'update_recommended') {
                return this.$t('Update recommended').toString();
            }

            return this.$t('Stable').toString();
        },
        healthBadgeClass(statusCode) {
            if (statusCode === 'stable') {
                return 'inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium border';
            }
            if (statusCode === 'update_recommended') {
                return 'inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700';
            }

            return 'inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700';
        },
        healthBadgeStyle(statusCode) {
            return statusCode === 'stable' ? this.greenBadgeStyle() : {};
        },
        environmentLabel(value) {
            if (value === 'production') {
                return this.$t('Production').toString();
            }
            if (value === 'staging') {
                return this.$t('Staging').toString();
            }

            return value || '-';
        }
    }
}
</script>
